import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Brain, AlertTriangle, TrendingDown, Users, Clock, RefreshCw, ChevronDown, ChevronUp, Zap, CheckCircle, Target, BarChart2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { toast } from "sonner";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_INTERVAL = 120000;
export default function AIAgent() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [expandedRec, setExpandedRec] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [countdown, setCountdown] = useState(120);

  const generateRecommendations = useCallback((d) => {
    const recs = [];
    const totalOnTime = d.worker_stats.reduce((a, w) => a + w.on_time, 0);
    const totalLate = d.worker_stats.reduce((a, w) => a + w.late, 0);
    const totalAbsent = d.worker_stats.reduce((a, w) => a + w.absent, 0);
    const totalScans = totalOnTime + totalLate + totalAbsent;
    const lateRate = totalScans > 0 ? Math.round(totalLate / totalScans * 100) : 0;

    const lateWorkers = d.worker_stats.filter(w => w.consecutive_late >= 2).sort((a,b) => b.consecutive_late - a.consecutive_late);
    if (lateWorkers.length > 0) {
      recs.push({
        id: "consecutive_late", type: "danger",
        priority: lateWorkers.some(w => w.consecutive_late >= 4) ? "critique" : "haute",
        icon: "clock", category: "Retards Consecutifs",
        title: lateWorkers.length + " ouvrier(s) avec retards consecutifs",
        summary: lateWorkers.map(w => ({ name: w.name, value: w.consecutive_late + " retards consecutifs", badge: w.consecutive_late >= 4 ? "critique" : "haute" })),
        prediction: "Les retards consecutifs signalent un probleme systematique. " + lateWorkers.length + " ouvrier(s) necessitent un entretien dans les 48h.",
        prescription: "1. Convoquer chaque ouvrier concerne pour un entretien confidentiel.\n2. Identifier la cause racine : transport, sante, motivation.\n3. Suivi quotidien sur 2 semaines.\n4. Procedure disciplinaire si aucune amelioration sous 1 semaine.",
        kpi: "Objectif : 0 retard consecutif en 2 semaines"
      });
    }

    const absentWorkers = d.worker_stats.filter(w => w.total_scans >= 3 && (w.absent / w.total_scans) >= 0.35).sort((a,b) => (b.absent/b.total_scans) - (a.absent/a.total_scans));
    if (absentWorkers.length > 0) {
      recs.push({
        id: "high_absence", type: "danger",
        priority: absentWorkers.some(w => w.absent/w.total_scans >= 0.6) ? "critique" : "haute",
        icon: "alert", category: "Absences Elevees",
        title: absentWorkers.length + " ouvrier(s) avec taux d absence critique",
        summary: absentWorkers.map(w => ({ name: w.name, value: Math.round(w.absent/w.total_scans*100) + "% absent (" + w.absent + "/" + w.total_scans + " jours)", badge: w.absent/w.total_scans >= 0.6 ? "critique" : "haute" })),
        prediction: "Un taux superieur a 35% place ces ouvriers en zone de risque. Statistiquement, +50% d absence = 70% de risque d abandon en 30 jours.",
        prescription: "1. Contact dans les 12h pour chaque ouvrier concerne.\n2. Verifier problemes de sante ou personnels.\n3. Mise en demeure si absence injustifiee.\n4. Initier remplacement si aucune reponse sous 48h.",
        kpi: "Objectif : reduire a moins de 20% d absence en 2 semaines"
      });
    }

    if (d.attendance_rate < 70) {
      recs.push({
        id: "global_attendance", type: "danger",
        priority: d.attendance_rate < 40 ? "critique" : "haute",
        icon: "trending", category: "Presence Globale",
        title: "Presence globale insuffisante : " + d.attendance_rate + "%",
        summary: null,
        prediction: "Un taux de " + d.attendance_rate + "% signale une crise organisationnelle. La capacite operationnelle sera compromise d ici 2 semaines.",
        prescription: "1. Reunion generale aujourd hui.\n2. Presenter les chiffres a l equipe.\n3. Systeme de prime de ponctualite.\n4. Objectif collectif de 80% la semaine suivante.",
        kpi: "Objectif semaine : atteindre 75% de presence"
      });
    }

    if (lateRate > 25) {
      recs.push({
        id: "global_late", type: "warning",
        priority: lateRate > 50 ? "haute" : "moyenne",
        icon: "clock", category: "Retard Global",
        title: "Taux de retard eleve : " + lateRate + "% des pointages",
        summary: null,
        prediction: "Un taux de " + lateRate + "% suggere un probleme systemique. Sans intervention, ce taux va augmenter par normalisation.",
        prescription: "1. Analyser la repartition des retards par heure.\n2. Sonder les ouvriers anonymement.\n3. Evaluer un decalage d horaire.\n4. Rappels automatiques 30min avant le pointage.",
        kpi: "Objectif : reduire a moins de 15% en 1 mois"
      });
    }

    if (d.worker_growth === 0 && d.total_workers > 0) {
      recs.push({
        id: "no_growth", type: "info",
        priority: "faible", icon: "users", category: "Effectifs",
        title: "Effectifs stagnants depuis 30 jours (" + d.total_workers + " ouvriers)",
        summary: null,
        prediction: "Stagnation dans un contexte d absences elevees = risque de sous-effectif chronique.",
        prescription: "1. Evaluer le besoin reel en effectifs.\n2. Lancer une prospection.\n3. Programme de referencement.",
        kpi: "Objectif : +2 nouveaux ouvriers ce mois"
      });
    }

    if (d.attendance_rate >= 80) {
      recs.push({
        id: "good_attendance", type: "success",
        priority: "positive", icon: "check", category: "Bonne Performance",
        title: "Excellent taux de presence : " + d.attendance_rate + "%",
        summary: null,
        prediction: "Equipe motivee et bien organisee. Maintenir ce cap permet une productivite optimale.",
        prescription: "1. Celebrer cette performance.\n2. Valoriser les ouvriers ponctuels.\n3. Fixer un objectif de 90%.",
        kpi: "Prochain objectif : 90% de presence"
      });
    }

    const topWorkers = d.worker_stats.filter(w => w.total_scans >= 3 && w.on_time / w.total_scans >= 0.8).sort((a,b) => b.on_time - a.on_time).slice(0, 5);
    if (topWorkers.length > 0) {
      recs.push({
        id: "top_performers", type: "success",
        priority: "positive", icon: "check", category: "Top Performers",
        title: topWorkers.length + " ouvrier(s) exemplaire(s)",
        summary: topWorkers.map(w => ({ name: w.name, value: Math.round(w.on_time/w.total_scans*100) + "% ponctualite (" + w.on_time + "/" + w.total_scans + " jours)", badge: "positive" })),
        prediction: "Ces ouvriers sont les piliers de la ponctualite et peuvent servir de modele.",
        prescription: "1. Les reconnaitre publiquement.\n2. Les impliquer comme ambassadeurs de ponctualite.\n3. Envisager des responsabilites supplementaires.",
        kpi: "Valoriser et maintenir ces performances"
      });
    }

    const priorityOrder = { "critique": 0, "haute": 1, "moyenne": 2, "faible": 3, "positive": 4 };
    recs.sort((a, b) => (priorityOrder[a.priority] ?? 5) - (priorityOrder[b.priority] ?? 5));
    setRecommendations(recs);
    setLastUpdate(new Date());
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/analysis`);
      setData(res.data);
      generateRecommendations(res.data);
      setLoading(false);
      setCountdown(120);
    } catch (e) {
      if (!silent) toast.error("Erreur de chargement");
      setLoading(false);
    }
  }, [generateRecommendations]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => prev <= 1 ? 120 : prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getPriorityStyle = (priority) => {
    switch(priority) {
      case "critique": return "bg-red-100 text-red-800 border-red-300";
      case "haute": return "bg-orange-100 text-orange-800 border-orange-300";
      case "moyenne": return "bg-amber-100 text-amber-800 border-amber-300";
      case "positive": return "bg-emerald-100 text-emerald-800 border-emerald-300";
      default: return "bg-blue-100 text-blue-800 border-blue-300";
    }
  };

  const getCardStyle = (type) => {
    switch(type) {
      case "danger": return "border-l-4 border-l-red-500 bg-red-50/50";
      case "warning": return "border-l-4 border-l-amber-500 bg-amber-50/50";
      case "success": return "border-l-4 border-l-emerald-500 bg-emerald-50/50";
      default: return "border-l-4 border-l-blue-500 bg-blue-50/50";
    }
  };

  const getIcon = (icon, type) => {
    const color = type === "danger" ? "text-red-500" : type === "warning" ? "text-amber-500" : type === "success" ? "text-emerald-500" : "text-blue-500";
    switch(icon) {
      case "clock": return React.createElement(Clock, {className: "h-5 w-5 " + color});
      case "trending": return React.createElement(TrendingDown, {className: "h-5 w-5 " + color});
      case "users": return React.createElement(Users, {className: "h-5 w-5 " + color});
      case "check": return React.createElement(CheckCircle, {className: "h-5 w-5 " + color});
      default: return React.createElement(AlertTriangle, {className: "h-5 w-5 " + color});
    }
  };

  const negativeRecs = recommendations.filter(r => r.type !== "success");
  const positiveRecs = recommendations.filter(r => r.type === "success");

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <Brain className="h-16 w-16 text-orange-500 mx-auto mb-4 animate-pulse" />
        <p className="text-slate-600 font-semibold">Proto IA en cours d analyse...</p>
      </div>
    </div>
  );

  const chartData = data?.weekly_counts.map(w => ({ date: w.date, presences: w.count, objectif: data.total_workers })) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-3 rounded-xl shadow-lg">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-manrope font-bold text-slate-900">Proto IA</h1>
            <p className="mt-1 text-slate-500 flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-orange-500" />Analyse automatique
            </p>
            {lastUpdate && <p className="text-xs text-slate-400 mt-1">Derniere analyse : {lastUpdate.toLocaleTimeString("fr-FR")} — Prochaine dans {countdown}s</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-slate-100 rounded-full px-4 py-2 text-sm text-slate-600 font-mono">⏱ {countdown}s</div>
          <Button onClick={() => fetchData()} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />Actualiser
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className={"border-2 " + (data.attendance_rate >= 70 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50")}>
            <CardContent className="pt-5">
              <p className="text-xs font-semibold text-slate-500 uppercase">Taux Presence</p>
              <p className={"text-3xl font-bold mt-1 " + (data.attendance_rate >= 70 ? "text-emerald-600" : "text-red-600")}>{data.attendance_rate}%</p>
              <p className="text-xs text-slate-400 mt-1">{data.attendance_rate >= 70 ? "✅ Normal" : "❌ Insuffisant"}</p>
            </CardContent>
          </Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Ouvriers</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{data.total_workers}</p>
            <p className="text-xs text-slate-400 mt-1">{data.worker_growth >= 0 ? "+" + data.worker_growth : data.worker_growth} ce mois</p>
          </CardContent></Card>
          <Card className={"border-2 " + (negativeRecs.filter(r => r.priority === "critique").length > 0 ? "border-red-200 bg-red-50" : "border-slate-200")}>
            <CardContent className="pt-5">
              <p className="text-xs font-semibold text-slate-500 uppercase">Alertes Critiques</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{negativeRecs.filter(r => r.priority === "critique").length}</p>
              <p className="text-xs text-slate-400 mt-1">{negativeRecs.length} alertes totales</p>
            </CardContent>
          </Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs font-semibold text-slate-500 uppercase">Ouvriers a Risque</p>
            <p className="text-3xl font-bold text-orange-600 mt-1">{data.worker_stats.filter(w => w.consecutive_late >= 2 || (w.total_scans >= 3 && w.absent/w.total_scans >= 0.35)).length}</p>
            <p className="text-xs text-slate-400 mt-1">Intervention requise</p>
          </CardContent></Card>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingDown className="h-5 w-5 text-orange-500" />Tendance Presences (Jours de Culte)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={d => new Date(d+"T12:00:00").toLocaleDateString("fr-FR", {day:"2-digit",month:"2-digit"})} stroke="#64748b" style={{fontSize:"11px"}} />
                  <YAxis stroke="#64748b" style={{fontSize:"11px"}} domain={[0, data.total_workers + 1]} />
                  <Tooltip labelFormatter={d => new Date(d+"T12:00:00").toLocaleDateString("fr-FR")} />
                  <Line type="monotone" dataKey="presences" stroke="#f97316" strokeWidth={2} dot={{fill:"#f97316",r:4}} name="Presences" />
                  <Line type="monotone" dataKey="objectif" stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Objectif" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BarChart2 className="h-5 w-5 text-orange-500" />Top 5 Performances</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[...data.worker_stats].sort((a,b) => b.on_time - a.on_time).slice(0,5).map(w => ({name: w.name.split(" ")[0], "A l heure": w.on_time, "Retard": w.late, "Absent": w.absent}))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" style={{fontSize:"11px"}} />
                  <YAxis style={{fontSize:"11px"}} />
                  <Tooltip />
                  <Legend style={{fontSize:"11px"}} />
                  <Bar dataKey="A l heure" fill="#f97316" />
                  <Bar dataKey="Retard" fill="#f59e0b" />
                  <Bar dataKey="Absent" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {negativeRecs.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-bold text-slate-900">Alertes et Interventions</h2>
            <span className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded-full">{negativeRecs.length} alertes</span>
          </div>
          <div className="space-y-3">
            {negativeRecs.map((rec) => (
              <Card key={rec.id} className={getCardStyle(rec.type)}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpandedRec(expandedRec === rec.id ? null : rec.id)}>
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-0.5">{getIcon(rec.icon, rec.type)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-400 uppercase">{rec.category}</span>
                          <span className={"text-xs font-bold px-2 py-0.5 rounded-full border " + getPriorityStyle(rec.priority)}>{rec.priority.toUpperCase()}</span>
                        </div>
                        <h3 className="font-bold text-slate-900 mt-1">{rec.title}</h3>
                        {rec.summary && (
                          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                            {rec.summary.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white/70 rounded px-3 py-1.5 border border-slate-100">
                                <span className="text-sm font-medium text-slate-700">{item.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">{item.value}</span>
                                  <span className={"text-xs font-bold px-1.5 py-0.5 rounded " + getPriorityStyle(item.badge)}>{item.badge}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button className="ml-3 text-slate-400 hover:text-slate-600 mt-1 shrink-0">
                      {expandedRec === rec.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>
                  </div>
                  {expandedRec === rec.id && (
                    <div className="mt-4 ml-8 space-y-3">
                      <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Analyse et Prediction</p>
                        <p className="text-sm text-slate-700 whitespace-pre-line">{rec.prediction}</p>
                      </div>
                      <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Plan d Action</p>
                        <p className="text-sm text-slate-700 whitespace-pre-line">{rec.prescription}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                        <p className="text-xs font-bold text-orange-600 flex items-center gap-1">KPI et Objectif</p>
                        <p className="text-sm text-orange-700 font-semibold mt-1">{rec.kpi}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {positiveRecs.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-6 w-6 text-emerald-500" />
            <h2 className="text-xl font-bold text-slate-900">Points Positifs</h2>
          </div>
          <div className="space-y-3">
            {positiveRecs.map((rec) => (
              <Card key={rec.id} className={getCardStyle(rec.type)}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpandedRec(expandedRec === rec.id ? null : rec.id)}>
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-0.5">{getIcon(rec.icon, rec.type)}</div>
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-slate-400 uppercase">{rec.category}</span>
                        <h3 className="font-bold text-slate-900 mt-1">{rec.title}</h3>
                        {rec.summary && (
                          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                            {rec.summary.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white/70 rounded px-3 py-1.5 border border-slate-100">
                                <span className="text-sm font-medium text-slate-700">{item.name}</span>
                                <span className="text-xs text-emerald-600 font-semibold">{item.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button className="ml-3 text-slate-400 hover:text-slate-600 mt-1">
                      {expandedRec === rec.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>
                  </div>
                  {expandedRec === rec.id && (
                    <div className="mt-4 ml-8 space-y-3">
                      <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Projection</p>
                        <p className="text-sm text-slate-700">{rec.prediction}</p>
                      </div>
                      <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Pour Aller Plus Loin</p>
                        <p className="text-sm text-slate-700 whitespace-pre-line">{rec.prescription}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-600">Prochain Objectif</p>
                        <p className="text-sm text-emerald-700 font-semibold mt-1">{rec.kpi}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {recommendations.length === 0 && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="text-5xl mb-3">OK</div>
            <h3 className="text-xl font-bold text-emerald-700">Systeme Optimal !</h3>
            <p className="text-emerald-600 mt-2">Tous les indicateurs sont dans les normes.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}